import { createSlice, createSelector } from '@reduxjs/toolkit';
import { SessionStateType } from '@business/entities/session';
import type { RootState } from './store';

const initialState: SessionStateType = {
  isAdmin: false,
  haveSubscription: true,
  userPermissions: [],
  notificationType: false,
  subscriberId: null,
  subscriptionPlanName: null,
  signUpCode: null,
  page: null,
  notificationData: null,
  isMediaUploadModalOpen: false,
  moduleForUpload: null,
  isFromRecipesPage: false,
  isFromFavoritePage: false,
  additionalPhotoMeta: {},
  firebaseToken: null,
  groupSubscriptionId: null,
  isPrivateDataVisible: true,
  memoryFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  photoFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  recipeFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  folderFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false,
    folderId: ''
  },
  momentFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  peopleFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  trashFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  sharingFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  sharedSubscriberId: null,
  additionalSubscriberId: null,
  shareRefreshCounter: 0,
  assetRefreshCounter: 0,
  uploadFilesSize: '0'
};

export const sessionStateSlice = createSlice({
  name: 'sessionStateSlice',
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
    },
    resetSessionState: () => {
      return initialState;
    },
    setSubscriberId: (state, action) => {
      state.subscriberId = action.payload.subscriberId;
    },
    setSharedSubscriberId: (state, action) => {
      state.sharedSubscriberId = action.payload;
    },
    setAdditionalSubscriberId: (state, action) => {
      state.additionalSubscriberId = action.payload;
    },
    setSubscriptionPlanName: (state, action) => {
      state.subscriptionPlanName = action.payload;
    },
    setSignUpCode: (state, action) => {
      state.signUpCode = action.payload;
    },
    setHaveSubscription: (state, action) => {
      state.haveSubscription = action.payload;
    },
    setIsMediaUploadModalOpen: (state, action) => {
      state.isMediaUploadModalOpen = action.payload;
    },
    setModuleForUpload: (state, action) => {
      state.moduleForUpload = action.payload;
    },
    logoutUser: state => {
      state.isAdmin = false;
      state.token = null;
      state.refreshToken = null;
      state.userPermissions = [];
      state.isMediaUploadModalOpen = false;
      state.subscriberId = null;
      state.sharedSubscriberId = null;
      state.additionalPhotoMeta = {};
      state.firebaseToken = null;
      state.subscriptionPlanName = null;
      state.signUpCode = null;
      state.memoryFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.photoFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.recipeFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.sharingFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.folderFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false,
        folderId: ''
      };
      state.momentFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.peopleFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.trashFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
    },
    setUserNotificationData: (state, action) => {
      state.page = action.payload.page;
      state.notificationData = action.payload.notificationData;
    },
    setIsFromRecipesPage: (state, action) => {
      state.isFromRecipesPage = action.payload;
    },
    setIsFromFavoritePage: (state, action) => {
      state.isFromFavoritePage = action.payload;
    },
    setAdditionalPhotoMeta: (state, action) => {
      state.additionalPhotoMeta = action.payload;
    },
    setFirebaseToken: (state, action) => {
      state.firebaseToken = action.payload;
    },
    setGroupSubscriptionId: (state, action) => {
      state.groupSubscriptionId = action.payload;
    },
    setMemoryFilterOptions: (state, action) => {
      state.memoryFilterOptions = action.payload;
    },
    setPhotoFilterOptions: (state, action) => {
      state.photoFilterOptions = action.payload;
    },
    setRecipeFilterOptions: (state, action) => {
      state.recipeFilterOptions = action.payload;
    },
    setSharingFilterOptions: (state, action) => {
      state.sharingFilterOptions = action.payload;
    },
    setFolderFilterOptions: (state, action) => {
      state.folderFilterOptions = action.payload;
    },
    setMomentFilterOptions: (state, action) => {
      state.momentFilterOptions = action.payload;
    },
    setPeopleFilterOptions: (state, action) => {
      state.peopleFilterOptions = action.payload;
    },
    setTrashFilterOptions: (state, action) => {
      state.trashFilterOptions = action.payload;
    },
    setIsPrivateDataVisible: (state, action) => {
      state.isPrivateDataVisible = action.payload;
    },
    incrementShareRefreshCounter: state => {
      state.shareRefreshCounter = (state.shareRefreshCounter ?? 0) + 1;
    },
    incrementAssetRefreshCounter: state => {
      state.assetRefreshCounter = (state.assetRefreshCounter ?? 0) + 1;
    },
    setUploadFilesSize: (state, action) => {
      state.uploadFilesSize = action.payload;
    }
  }
});

export const {
  setToken,
  resetSessionState,
  logoutUser,
  setSubscriberId,
  setSharedSubscriberId,
  setAdditionalSubscriberId,
  setSubscriptionPlanName,
  setSignUpCode,
  setHaveSubscription,
  setUserNotificationData,
  setIsMediaUploadModalOpen,
  setModuleForUpload,
  setIsFromRecipesPage,
  setIsFromFavoritePage,
  setAdditionalPhotoMeta,
  setFirebaseToken,
  setMemoryFilterOptions,
  setPhotoFilterOptions,
  setRecipeFilterOptions,
  setFolderFilterOptions,
  setMomentFilterOptions,
  setPeopleFilterOptions,
  setTrashFilterOptions,
  setSharingFilterOptions,
  setGroupSubscriptionId,
  setIsPrivateDataVisible,
  incrementShareRefreshCounter,
  incrementAssetRefreshCounter,
  setUploadFilesSize
} = sessionStateSlice.actions;
export const getToken = (state: RootState) => state.sessionStateSlice?.token;
export const getRefreshToken = (state: RootState) =>
  state.sessionStateSlice?.refreshToken;
export const getSubscriberId = (state: RootState) =>
  state.sessionStateSlice?.subscriberId;
export const getHaveSubscription = (state: RootState) =>
  state.sessionStateSlice?.haveSubscription;
export const getSharedSubscriberId = (state: RootState) =>
  state.sessionStateSlice?.sharedSubscriberId;
export const getAdditionalSubscriberId = (state: RootState) =>
  state.sessionStateSlice?.additionalSubscriberId;
export const getIsMediaUploadModalOpen = (state: RootState) =>
  state.sessionStateSlice?.isMediaUploadModalOpen;
export const getModuleForUpload = (state: RootState) =>
  state.sessionStateSlice?.moduleForUpload;
export const getGroupSubscriptionId = (state: RootState) =>
  state.sessionStateSlice?.groupSubscriptionId;
export const getIsPrivateDataVisible = (state: RootState) =>
  state.sessionStateSlice?.isPrivateDataVisible;
export const getUserNotificationData = createSelector(
  [
    (state: RootState) => state.sessionStateSlice?.page,
    (state: RootState) => state.sessionStateSlice?.notificationData
  ],
  (page, notificationData) => ({
    page,
    notificationData
  })
);
export const getIsFromRecipesPage = (state: RootState) =>
  state.sessionStateSlice?.isFromRecipesPage;
export const getIsFromFavoritePage = (state: RootState) =>
  state.sessionStateSlice?.isFromFavoritePage;
export const getAdditionalPhotoMeta = (state: RootState) =>
  state.sessionStateSlice?.additionalPhotoMeta;
export const getFirebaseToken = (state: RootState) =>
  state.sessionStateSlice?.firebaseToken;
export const getMemoryFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.memoryFilterOptions;
export const getPhotoFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.photoFilterOptions;
export const getRecipeFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.recipeFilterOptions;
export const getFolderFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.folderFilterOptions;
export const getMomentFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.momentFilterOptions;
export const getPeopleFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.peopleFilterOptions;
export const getTrashFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.trashFilterOptions;
export const getSharingFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.sharingFilterOptions;
export const getSubscriptionPlanName = (state: RootState) =>
  state.sessionStateSlice?.subscriptionPlanName;
export const getSignUpCode = (state: RootState) =>
  state.sessionStateSlice?.signUpCode;
export const getShareRefreshCounter = (state: RootState) =>
  state.sessionStateSlice?.shareRefreshCounter ?? 0;
export const getAssetRefreshCounter = (state: RootState) =>
  state.sessionStateSlice?.assetRefreshCounter ?? 0;
export const getUploadFilesSize = (state: RootState) =>
  state.sessionStateSlice?.uploadFilesSize ?? '0';
